import type { Database, Tables } from "@/integrations/supabase/types";
import i18n from "@/lib/i18n";

export type Listing = Tables<"listings"> & { listing_images: Tables<"listing_images">[] };
export type PublicListing = Database["public"]["Views"]["public_listings"]["Row"] & {
  listing_images: Tables<"listing_images">[];
};

export type ListingStatus = Database["public"]["Enums"]["listing_status"];
export type ApartmentType = Database["public"]["Enums"]["apartment_type"];
export type ListingType = Database["public"]["Enums"]["listing_type"];
export type PropertyType = Database["public"]["Enums"]["property_type"];
export type BuildingType = Database["public"]["Enums"]["building_type"];
export type KitchenType = Database["public"]["Enums"]["kitchen_type"];
export type FurnishedState = Database["public"]["Enums"]["furnished_state"];
export type AdvertiserType = Database["public"]["Enums"]["advertiser_type"];
export type LeadStatus = Database["public"]["Enums"]["lead_status"];
export type CurrencyCode = "USD" | "TRY";
export type BuildingAge = "new" | "1_5" | "5_10" | "10_15" | "15_20" | "20_25";
export const buildingAgeOptions: BuildingAge[] = ["new", "1_5", "5_10", "10_15", "15_20", "20_25"];
export const formatBuildingAge = (b: BuildingAge) => i18n.t(`submit.buildingAge.options.${b}`);

export const apartmentTypeOptions: ApartmentType[] = ["studio", "1+1", "2+1", "3+1", "4+1", "5+2", "villa", "duplex", "other"];
export const listingTypeOptions: ListingType[] = ["rent", "sale"];
export const propertyTypeOptions: PropertyType[] = ["apartment", "villa", "floor", "other"];
export const listingStatusOptions: ListingStatus[] = ["available", "reserved", "sold", "rented"];
export const advertiserTypeOptions: AdvertiserType[] = ["owner", "agency", "company", "broker"];
export const furnishedOptions: FurnishedState[] = ["yes", "no", "semi"];
export const leadStatusOptions: LeadStatus[] = ["new", "contacted", "viewing_scheduled", "negotiating", "closed_won", "closed_lost"];
export const currencyOptions: CurrencyCode[] = ["USD", "TRY"];

const tr = (k: string) => i18n.t(k);

export const formatApartmentType = (t: ApartmentType) => tr(`apartmentType.${t}`);
export const formatListingType = (t: ListingType) => tr(`listingType.${t}`);
export const formatRequestListingType = (t: ListingType) => tr(`requestListingType.${t}`);
export const formatPropertyType = (t: PropertyType) => tr(`propertyType.${t}`);
export const formatStatus = (s: ListingStatus) => tr(`listingStatus.${s}`);
export const formatLeadStatus = (s: LeadStatus) => tr(`lead.${s}`);
export const formatFurnished = (f: FurnishedState) => tr(`furnished.${f}`);
export const formatAdvertiserType = (a: AdvertiserType) => tr(`advertiser.${a}`);
export const formatBuildingType = (b: BuildingType) => tr(`building.${b}`);
export const formatKitchenType = (k: KitchenType) => tr(`kitchen.${k}`);

export const formatPrice = (price: number, currency: CurrencyCode = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 0 }).format(price);

// Auto-calculated rental commission: 12% of annual rent
export const computeCommission = (monthlyPrice: number, rate = 0.12) => Math.round(monthlyPrice * 12 * rate);

// Extract approximate lat/lng from a Google Maps URL
export const extractLatLngFromMapsUrl = (url: string | null | undefined): { lat: number; lng: number } | null => {
  if (!url) return null;
  // matches !3d<lat>!4d<lng> or @<lat>,<lng>
  const at = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (at) return { lat: parseFloat(at[1]), lng: parseFloat(at[2]) };
  const dq = url.match(/!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/);
  if (dq) return { lat: parseFloat(dq[1]), lng: parseFloat(dq[2]) };
  const q = url.match(/[?&]q=(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (q) return { lat: parseFloat(q[1]), lng: parseFloat(q[2]) };
  return null;
};
